<script setup>
import { computed, inject, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  useStore,
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store';
import {
  getLastMessage,
  getReplyAgentName,
} from 'dashboard/helper/conversationHelper';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import Avatar from 'next/avatar/Avatar.vue';
import MessagePreview from './MessagePreview.vue';
import InboxName from '../InboxName.vue';
import ConversationContextMenu from './contextMenu/Index.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import PriorityMark from './PriorityMark.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';

const props = defineProps({
  activeLabel: { type: String, default: '' },
  chat: { type: Object, default: () => ({}) },
  hideInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  showAssignee: { type: Boolean, default: false }, // eslint-disable-line vue/no-unused-properties

  conversationType: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  enableContextMenu: { type: Boolean, default: false },
  allowedContextMenuOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'contextMenuToggle',
  'assignAgent',
  'assignLabel',
  'removeLabel',
  'assignTeam',
  'markAsUnread',
  'markAsRead',
  'assignPriority',
  'updateConversationStatus',
  'deleteConversation',
  'selectConversation',
  'deSelectConversation',
]);

// Eltafouk (Comments cleanup, phase 3): section-level flag provided once by
// ConversationView. Defaults to false so cards rendered outside that tree
// (previous-DM list, contact history, stories) behave exactly as before.
const isCommentSection = inject(
  'isCommentSection',
  computed(() => false)
);

// Comment threads have NO status/lifecycle workflow (Eltafouk triages with
// تم/محتاج-رد labels). In a comment view restrict the card's right-click menu
// to labels, read-toggle and open/copy — hiding resolve/reopen/pending, snooze,
// priority, assign-agent and assign-team. Outside comments we pass the caller's
// whitelist through untouched (empty array = show everything).
const effectiveContextMenuOptions = computed(() =>
  isCommentSection.value
    ? ['label', 'mark-as-read', 'mark-as-unread', 'open-new-tab', 'copy-link']
    : props.allowedContextMenuOptions
);

const router = useRouter();
const store = useStore();

const hovered = ref(false);
const showContextMenu = ref(false);
const contextMenu = ref({
  x: null,
  y: null,
});

const currentChat = useMapGetter('getSelectedChat');
const inboxesList = useMapGetter('inboxes/getInboxes');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');

const chatMetadata = computed(() => props.chat.meta || {});

const senderId = computed(() => chatMetadata.value.sender?.id);

// Reactive getter so the card updates when the contact record changes via a
// websocket update. The getContact getter already returns {} for a missing or
// undefined id, preserving the no-sender fallback.
const currentContact = useFunctionGetter('contacts/getContact', senderId);

const isActiveChat = computed(() => {
  return currentChat.value.id === props.chat.id;
});

const unreadCount = computed(() => props.chat.unread_count);

const hasUnread = computed(() => unreadCount.value > 0);

// On the Mentions list, flag rows whose mention the agent hasn't opened yet —
// distinct from the conversation's own unread-message state.
const unreadMentionIds = useMapGetter('conversationStats/getUnreadMentionIds');
const isUnreadMention = computed(
  () =>
    props.conversationType === 'mention' &&
    (unreadMentionIds.value?.includes(props.chat.id) ?? false)
);

const isInboxNameVisible = computed(() => !activeInbox.value);

const lastMessageInChat = computed(() => getLastMessage(props.chat));

// First name of the agent who last replied to the customer. When the newest
// in-store message is a human-agent reply (message_type 1 = outgoing, User
// sender, non-private) we show it instantly so the card updates the moment a
// reply is sent. For panel/relay comment+DM replies the Chatwoot sender is the
// page-relay User («الصفحة»), so we read the real agent off the message's
// content_attributes.eltafouk_agent_name stamp first (via getReplyAgentName) —
// matching the message bubble and the backend — and fall back to the raw sender
// only for plain DM agent replies that carry no stamp. Otherwise we use the
// backend value, which already prefers the last human agent and only resolves
// to 'Bot' when no human has replied — and the ADD_MESSAGE handler keeps it
// fresh in real-time. Automated templates / bot replies never flip the card to
// 'Bot' over the real agent.
const lastReplyName = computed(() => {
  const last = lastMessageInChat.value;
  if (
    last &&
    last.message_type === 1 &&
    last.sender_type === 'User' &&
    !last.private
  ) {
    const name = getReplyAgentName(last);
    if (name) return name.split(' ')[0];
  }
  const backendName = props.chat.last_reply_agent_name;
  return backendName ? backendName.split(' ')[0] : null;
});

const voiceCallData = computed(() => ({
  status: props.chat.additional_attributes?.call_status,
  direction: props.chat.additional_attributes?.call_direction,
}));

const inboxId = computed(() => props.chat.inbox_id);

const inbox = computed(() => {
  return inboxId.value ? store.getters['inboxes/getInbox'](inboxId.value) : {};
});

// Eltafouk: is THIS card a comment thread? FB commenters carry no SLA policy /
// priority, so the SLA chip and priority mark below are meaningless on them.
// Derived per-card (durable inbox flag + comment_id fallback), mirroring
// ConversationHeader, so it's correct even outside the التعليقات section.
const isCommentCard = computed(
  () =>
    Boolean(inbox.value?.is_comment_inbox) ||
    props.chat?.custom_attributes?.comment_id != null
);

const isAWhatsAppChannel = computed(() => {
  if (!inbox.value) return false;
  const channelType = inbox.value.channel_type || inbox.value.channelType;
  const medium = inbox.value.medium || '';
  return (
    channelType === 'Channel::Whatsapp' ||
    (channelType === 'Channel::TwilioSms' && medium === 'whatsapp')
  );
});

const formattedPhoneNumber = computed(() => {
  const name = currentContact.value.name || '';
  // Show the WhatsApp number this conversation actually came in on
  // (contact_inbox.source_id) rather than the contact's saved phone, so cards
  // for the same contact's different numbers are distinguishable — matches the
  // conversation header. Fall back to the contact's saved phone when absent.
  const sourceId = chatMetadata.value?.source_id;
  const phone =
    (isAWhatsAppChannel.value && sourceId) || currentContact.value.phone_number;

  if (isAWhatsAppChannel.value && phone) {
    // Normalize both the phone and the name to the same local Egyptian form
    // before deduping. Egypt E.164 is "20" + "1XXXXXXXXX"; the local form is
    // "0" + "1XXXXXXXXX". Non-Egyptian numbers keep their raw digits, so a name
    // holding the number in either local or E.164 form dedups correctly.
    const toLocalEgypt = value => {
      const digits = String(value || '').replace(/\D/g, '');
      return /^20\d{10,}$/.test(digits) ? `0${digits.substring(2)}` : digits;
    };
    const cleanPhone = toLocalEgypt(phone);
    const nameDigits = toLocalEgypt(name);
    if (cleanPhone && nameDigits !== cleanPhone && !name.includes(cleanPhone)) {
      return cleanPhone;
    }
  }

  return null;
});

// Eltafouk: when a comment thread has a reply from someone other than the
// conversation's contact (the OP), surface that latest commenter as the
// primary title and demote the OP to a sub-text "رد على {OP}". n8n WF1
// formats reply content as "[رد من {name}]: {body}" so we parse that
// prefix off the last incoming message. Only applies to comment cards;
// DM cards use the default contact-name title untouched.
const latestCommenter = computed(() => {
  const lastMsg = lastMessageInChat.value;
  if (!lastMsg || lastMsg.message_type !== 0) return null; // incoming only
  const content = String(lastMsg.content || '');
  const match = content.match(/^\[رد من\s+([^\]]+)\]\s*:/);
  if (!match) return null;
  const name = match[1].trim();
  if (!name || name === currentContact.value?.name) return null;
  return name;
});

// Eltafouk: source platform/page badge shown above the contact name for comment inboxes.
const ELTAFOUK_FB_PAGES = {
  111233573822014: 'التفوق للثانوية العامة',
  282599361610166: 'كتاب التفوق',
  909009775622795: 'التفوق للنشر والتوزيع',
};
const commentSourceInfo = computed(() => {
  const ca =
    props.chat?.custom_attributes || props.chat?.customAttributes || {};
  const platform = ca.platform;
  const postId = ca.post_id || ca.postId || '';
  if (!platform) return null;
  if (platform === 'instagram') {
    return { platform: 'instagram', label: 'انستجرام', color: '#E4405F' };
  }
  const pageId = String(postId).split('_')[0];
  const pageName = ELTAFOUK_FB_PAGES[pageId];
  return {
    platform: 'facebook',
    label: pageName ? `فيسبوك · ${pageName}` : 'فيسبوك',
    color: '#1877F2',
  };
});
const commentSourceBadgeStyle = computed(() => {
  if (!commentSourceInfo.value) return {};
  return {
    backgroundColor: `${commentSourceInfo.value.color}1A`,
    color: commentSourceInfo.value.color,
  };
});

// Eltafouk: surface comment moderation state on the card. Three distinct
// situations, all sourced from the conversation's custom_attributes:
//   * comment_deleted_at      → the customer themselves removed the
//                               comment on FB (panel sets this when the
//                               Graph fetch returns is_deleted=true OR
//                               when an agent uses the delete action).
//                               Authored-by populated only when an agent
//                               did it (via comment_deleted_by).
//   * comment_hidden_at + by  → an agent hid the comment from the panel.
//                               Tooltip carries the agent's name and the
//                               reason text from comment_hidden_reason.
// Both states mark the conversation read (handled server-side) and the
// unread count badge below stays suppressed.
const commentModerationState = computed(() => {
  const ca =
    props.chat?.custom_attributes || props.chat?.customAttributes || {};
  if (ca.comment_deleted_at) {
    const by =
      typeof ca.comment_deleted_by === 'string' ? ca.comment_deleted_by : null;
    const reason =
      typeof ca.comment_deleted_reason === 'string'
        ? ca.comment_deleted_reason
        : null;
    // Agent-initiated delete vs customer-side delete: only show the
    // agent label when we actually have a name. Without `by` we treat
    // it as the customer-deleted case (preserves prior behavior).
    return {
      kind: 'deleted',
      label: by ? 'تم حذفه' : 'تم مسحه',
      tooltip: by
        ? `حذفه ${by}${reason ? `\nالسبب: ${reason}` : ''}`
        : 'قام المستخدم بمسح الكومنت على فيسبوك / انستجرام',
      classes:
        'bg-[#FEE2E2] text-[#B91C1C] dark:bg-[#7F1D1D]/40 dark:text-[#FCA5A5]',
      icon: 'dismiss-circle',
    };
  }
  if (ca.comment_hidden_at) {
    const by =
      typeof ca.comment_hidden_by === 'string' ? ca.comment_hidden_by : null;
    const reason =
      typeof ca.comment_hidden_reason === 'string'
        ? ca.comment_hidden_reason
        : null;
    return {
      kind: 'hidden',
      label: 'تم إخفاؤه',
      tooltip: by
        ? `أخفاه ${by}${reason ? `\nالسبب: ${reason}` : ''}`
        : 'تم إخفاء التعليق من الصفحة',
      classes:
        'bg-[#FEF3C7] text-[#92400E] dark:bg-[#78350F]/40 dark:text-[#FCD34D]',
      icon: 'eye-hide',
    };
  }
  return null;
});
const isCommentModerated = computed(
  () => commentModerationState.value !== null
);
// Back-compat aliases used below in the template — keep the existing
// names so the diff stays minimal.
const isCommentDeleted = isCommentModerated;

// «اترد بالخاص»: the agent answered this comment privately (DM) with no public
// comment — the FB error-368-safe default reply flow. The panel stamps
// custom_attributes.replied_via_dm_at; we render a pill the same way the
// moderation pills do. Moderation takes visual priority (v-else-if in the
// template), so a hidden/deleted comment shows its moderation pill instead.
const repliedViaDmState = computed(() => {
  const ca =
    props.chat?.custom_attributes || props.chat?.customAttributes || {};
  if (!ca.replied_via_dm_at) return null;
  const by =
    typeof ca.replied_via_dm_by === 'string' ? ca.replied_via_dm_by : null;
  return {
    // Channel + agent inline on the card face (green = DM). Falls back to the
    // bare channel label when no agent name was stamped. First name only on the
    // pill (full name stays in the tooltip) so a long name doesn't break the
    // card layout — matches how message bubbles show the agent.
    label: by ? `خاص · ${by.split(' ')[0]}` : 'اترد بالخاص',
    tooltip: by ? `رد على الخاص: ${by}` : 'تم الرد على الكومنت عبر رسالة خاصة',
    classes:
      'bg-[#DCFCE7] text-[#166534] dark:bg-[#14532D]/40 dark:text-[#86EFAC]',
    icon: 'chat',
  };
});

// «رد عام»: the agent answered this comment with a PUBLIC reply on the post.
// The panel stamps custom_attributes.replied_publicly_at + _by. Rendered in a
// distinct blue (vs the green DM pill) so the channel reads at a glance, with
// the agent inline on the card face. Public takes priority over DM in the
// template when a comment got both; moderation still outranks both.
const repliedPublicState = computed(() => {
  const ca =
    props.chat?.custom_attributes || props.chat?.customAttributes || {};
  if (!ca.replied_publicly_at) return null;
  const by =
    typeof ca.replied_publicly_by === 'string' ? ca.replied_publicly_by : null;
  return {
    label: by ? `رد عام · ${by.split(' ')[0]}` : 'اترد برد عام',
    tooltip: by ? `رد عام: ${by}` : 'تم الرد على الكومنت برد عام',
    classes:
      'bg-[#DBEAFE] text-[#1E40AF] dark:bg-[#1E3A8A]/40 dark:text-[#93C5FD]',
    icon: 'arrow-reply',
  };
});

// «فشل الإرسال»: a reply (DM, or a requested public ack) could NOT be delivered,
// so the send-reply endpoint re-flagged this conversation (custom_attributes
// .send_failed_at + marked it unread). Rendered as a RED pill with TOP priority
// over every other pill — it means the agent must act. Cleared server-side once
// the op is proven live (reconciler) or a successful retry lands (mark_replied).
const sendFailedState = computed(() => {
  const ca =
    props.chat?.custom_attributes || props.chat?.customAttributes || {};
  if (!ca.send_failed_at) return null;
  const code =
    typeof ca.send_failed_error_code === 'string'
      ? ca.send_failed_error_code
      : '';
  const labelByCode = { 10903: 'تعذّر إرسال الخاص', 368: 'محظور مؤقتاً' };
  const label = labelByCode[code] || 'فشل الإرسال';
  const reason =
    typeof ca.send_failed_reason === 'string' ? ca.send_failed_reason : null;
  return {
    label,
    tooltip: reason || 'فشل إرسال الرد — محتاج إعادة محاولة',
    classes:
      'bg-[#FEE2E2] text-[#DC2626] dark:bg-[#7F1D1D]/40 dark:text-[#FCA5A5]',
    icon: 'warning',
  };
});

const showInboxName = computed(() => {
  // Eltafouk: when our colored platform/page pill (commentSourceInfo) renders,
  // suppress Chatwoot's default InboxName badge so the same page name isn't
  // shown twice on the same card.
  if (commentSourceInfo.value) return false;
  return (
    !props.hideInboxName &&
    isInboxNameVisible.value &&
    inboxesList.value.length > 1 &&
    !!inbox.value?.id
  );
});

const showMetaSection = computed(() => {
  return showInboxName.value || (props.chat.priority && !isCommentCard.value);
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const showLabelsSection = computed(() => {
  return (
    props.chat.labels?.length > 0 ||
    (hasSlaPolicyId.value && !isCommentCard.value)
  );
});

const messagePreviewClass = computed(() => {
  return [
    hasUnread.value ? 'font-semibold text-n-slate-12' : 'text-n-slate-9',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});

const conversationPath = computed(() => {
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.chat.id,
      label: props.activeLabel,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  );
});

const onCardClick = e => {
  const path = conversationPath.value;
  if (!path) return;

  // Handle Ctrl/Cmd + Click for new tab
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  // Skip if already active
  if (isActiveChat.value) return;

  router.push({ path });
};

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, props.chat.inbox_id);
  } else {
    emit('deSelectConversation', props.chat.id, props.chat.inbox_id);
  }
};

const openContextMenu = e => {
  if (!props.enableContextMenu) return;
  e.preventDefault();
  emit('contextMenuToggle', true);
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit('updateConversationStatus', props.chat.id, status, snoozedUntil);
};

const onAssignAgent = agent => {
  emit('assignAgent', agent, [props.chat.id]);
  closeContextMenu();
};

const onAssignLabel = label => {
  emit('assignLabel', [label.title], [props.chat.id]);
};

const onRemoveLabel = label => {
  emit('removeLabel', [label.title], [props.chat.id]);
};

const onAssignTeam = team => {
  emit('assignTeam', team, props.chat.id);
  closeContextMenu();
};

const markAsUnread = () => {
  emit('markAsUnread', props.chat.id);
  closeContextMenu();
};

const markAsRead = () => {
  emit('markAsRead', props.chat.id);
  closeContextMenu();
};

const assignPriority = priority => {
  emit('assignPriority', priority, props.chat.id);
  closeContextMenu();
};

const deleteConversation = () => {
  emit('deleteConversation', props.chat.id);
  closeContextMenu();
};
</script>

<template>
  <div
    class="relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full py-0 border-transparent border-solid cursor-pointer conversation hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group transition-all duration-150"
    :class="{
      'active animate-card-select bg-n-background ltr:border-l-[3px] ltr:!border-l-n-brand rtl:border-r-[3px] rtl:!border-r-n-brand':
        isActiveChat,
      'bg-n-slate-2': selected,
      'px-0': compact,
      'px-3': !compact,
      'ltr:border-l-[3px] ltr:border-l-transparent rtl:border-r-[3px] rtl:border-r-transparent':
        !isActiveChat,
    }"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <div
      class="relative"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        :class="
          !showMetaSection && !commentSourceInfo && !isCommentDeleted
            ? 'mt-4'
            : 'mt-8'
        "
        hide-offline-status
        rounded-full
      >
        <template #overlay="{ size }">
          <label
            v-if="(hovered || selected) && !isCommentSection"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <input
              :value="selected"
              :checked="selected"
              class="!m-0 cursor-pointer"
              type="checkbox"
              @change="onSelectConversation($event.target.checked)"
            />
          </label>
        </template>
      </Avatar>
    </div>
    <div class="px-0 py-3 border-b flex-1 border-n-weak min-w-0">
      <div
        v-if="showMetaSection"
        class="flex items-center min-w-0 gap-1"
        :class="{
          'ltr:ml-2 rtl:mr-2': !compact,
          'mx-2': compact,
        }"
      >
        <InboxName v-if="showInboxName" :inbox="inbox" class="flex-1 min-w-0" />
        <div
          class="flex items-center gap-2 flex-shrink-0"
          :class="{
            'flex-1 justify-between': !showInboxName,
          }"
        >
          <PriorityMark :priority="chat.priority" class="flex-shrink-0" />
        </div>
      </div>
      <div
        v-if="
          commentSourceInfo ||
          sendFailedState ||
          isCommentDeleted ||
          repliedPublicState ||
          repliedViaDmState
        "
        class="mx-2 mt-1 flex items-center min-w-0 gap-1.5"
      >
        <div
          v-if="commentSourceInfo"
          class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium leading-none min-w-0 max-w-full"
          :style="commentSourceBadgeStyle"
        >
          <svg
            v-if="commentSourceInfo.platform === 'facebook'"
            viewBox="0 0 24 24"
            class="size-3 flex-shrink-0"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              d="M24 12.073C24 5.404 18.627 0 12 0S0 5.404 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.356c0-3.016 1.792-4.682 4.533-4.682 1.312 0 2.686.235 2.686.235v2.965h-1.514c-1.49 0-1.955.93-1.955 1.886v2.265h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"
            />
          </svg>
          <svg
            v-else
            viewBox="0 0 24 24"
            class="size-3 flex-shrink-0"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.336 3.608 1.311.975.975 1.249 2.242 1.311 3.608.058 1.266.07 1.646.07 4.85 0 3.205-.012 3.585-.07 4.85-.062 1.366-.336 2.633-1.311 3.608-.975.975-2.242 1.249-3.608 1.311-1.266.058-1.645.07-4.85.07-3.204 0-3.584-.012-4.85-.07-1.366-.062-2.633-.336-3.608-1.311-.975-.975-1.249-2.242-1.311-3.608-.058-1.266-.07-1.645-.07-4.85 0-3.204.012-3.584.07-4.85.062-1.366.336-2.633 1.311-3.608.975-.975 2.242-1.249 3.608-1.311 1.266-.058 1.646-.07 4.85-.07zM12 0C8.741 0 8.332.014 7.052.072 5.775.13 4.903.333 4.14.63a5.876 5.876 0 00-2.126 1.384A5.876 5.876 0 00.63 4.14C.333 4.903.131 5.775.072 7.052.014 8.332 0 8.741 0 12s.014 3.668.072 4.948c.059 1.277.261 2.149.558 2.912a5.876 5.876 0 001.384 2.126 5.876 5.876 0 002.126 1.384c.763.297 1.635.499 2.912.558C8.332 23.986 8.741 24 12 24s3.668-.014 4.948-.072c1.277-.059 2.149-.261 2.912-.558a5.876 5.876 0 002.126-1.384 5.876 5.876 0 001.384-2.126c.297-.763.499-1.635.558-2.912.058-1.28.072-1.689.072-4.948s-.014-3.668-.072-4.948c-.059-1.277-.261-2.149-.558-2.912a5.876 5.876 0 00-1.384-2.126A5.876 5.876 0 0019.86.63c-.763-.297-1.635-.499-2.912-.558C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zm0 10.162a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"
            />
          </svg>
          <span class="truncate">{{ commentSourceInfo.label }}</span>
        </div>
        <span
          v-if="sendFailedState"
          class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium leading-none min-w-0 max-w-[160px]"
          :class="sendFailedState.classes"
          :title="sendFailedState.tooltip"
        >
          <fluent-icon
            :icon="sendFailedState.icon"
            size="12"
            class="flex-shrink-0"
          />
          <span class="truncate">{{ sendFailedState.label }}</span>
        </span>
        <span
          v-else-if="commentModerationState"
          class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium leading-none flex-shrink-0"
          :class="commentModerationState.classes"
          :title="commentModerationState.tooltip"
        >
          <fluent-icon :icon="commentModerationState.icon" size="12" />
          <span>{{ commentModerationState.label }}</span>
        </span>
        <span
          v-else-if="repliedPublicState"
          class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium leading-none min-w-0 max-w-[150px]"
          :class="repliedPublicState.classes"
          :title="repliedPublicState.tooltip"
        >
          <fluent-icon
            :icon="repliedPublicState.icon"
            size="12"
            class="flex-shrink-0"
          />
          <span class="truncate">{{ repliedPublicState.label }}</span>
        </span>
        <span
          v-else-if="repliedViaDmState"
          class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-medium leading-none min-w-0 max-w-[150px]"
          :class="repliedViaDmState.classes"
          :title="repliedViaDmState.tooltip"
        >
          <fluent-icon
            :icon="repliedViaDmState.icon"
            size="12"
            class="flex-shrink-0"
          />
          <span class="truncate">{{ repliedViaDmState.label }}</span>
        </span>
      </div>
      <div
        class="flex items-center my-0 mx-2 pt-0.5 flex-1 min-w-0 ltr:pr-16 rtl:pl-16"
      >
        <span
          v-if="isUnreadMention"
          class="me-1.5 inline-block size-2 flex-shrink-0 rounded-full bg-n-blue-9"
        />
        <div
          v-if="formattedPhoneNumber"
          class="inline-flex items-stretch border border-[#BBDEFB] dark:border-[#1E40AF] rounded max-w-full overflow-hidden shadow-sm"
        >
          <h4
            class="conversation--user text-xs px-2 py-0.5 capitalize text-ellipsis overflow-hidden whitespace-nowrap min-w-0 bg-[#E3F2FD] dark:bg-[#1E3A8A] text-[#1565C0] dark:text-[#93C5FD] font-bold"
          >
            {{ currentContact.name }}
          </h4>
          <span
            class="px-2 py-0.5 text-[10px] ltr:border-l rtl:border-r border-[#BBDEFB] dark:border-[#1E40AF] flex-shrink-0 flex items-center bg-[#E8F5E9] dark:bg-[#064E3B] text-[#2E7D32] dark:text-[#6EE7B7] font-bold"
            dir="ltr"
          >
            {{ formattedPhoneNumber }}
          </span>
        </div>
        <div v-else class="min-w-0 flex-1">
          <h4
            class="conversation--user text-sm capitalize text-ellipsis overflow-hidden whitespace-nowrap min-w-0 leading-tight"
            :class="
              hasUnread
                ? 'font-bold text-n-slate-12'
                : 'font-medium text-n-slate-12'
            "
          >
            {{ latestCommenter || currentContact.name }}
          </h4>
          <div
            v-if="latestCommenter"
            class="text-[11px] leading-tight text-n-slate-10 truncate mt-px"
          >
            {{ `رد على ${currentContact.name}` }}
          </div>
        </div>
      </div>
      <VoiceCallStatus
        v-if="voiceCallData.status"
        key="voice-status-row"
        :status="voiceCallData.status"
        :direction="voiceCallData.direction"
        :message-preview-class="messagePreviewClass"
      />
      <MessagePreview
        v-else-if="lastMessageInChat"
        key="message-preview"
        :message="lastMessageInChat"
        dir="auto"
        class="my-0 mx-2 leading-6 h-6 flex-1 min-w-0 text-xs"
        :class="messagePreviewClass"
      />
      <p
        v-else
        key="no-messages"
        class="text-n-slate-11 text-xs my-0 mx-2 leading-6 h-6 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
        :class="messagePreviewClass"
      >
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span class="mx-0.5">
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <div
        class="absolute flex flex-col ltr:right-3 rtl:left-3"
        :class="showMetaSection ? 'top-8' : 'top-4'"
      >
        <span
          class="relative ml-auto font-normal leading-4 text-xxs"
          :class="hasUnread ? 'text-[#25D366]' : 'text-n-slate-10'"
        >
          <span
            v-if="lastReplyName"
            :title="lastReplyName"
            class="absolute bottom-full mb-0.5 ltr:right-0 rtl:left-0 max-w-[110px] truncate whitespace-nowrap font-medium leading-4 text-xxs text-n-slate-11"
          >
            {{ lastReplyName }}
          </span>
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
          />
        </span>
        <span
          class="shadow-lg rounded-full text-xxs font-bold h-[18px] leading-[18px] ltr:ml-auto rtl:mr-auto mt-1 min-w-[18px] px-1.5 py-0 text-center text-white bg-[#25D366]"
          :class="hasUnread && !isCommentDeleted ? 'block' : 'hidden'"
          dir="ltr"
        >
          {{ unreadCount > 9 ? '9+' : unreadCount }}
        </span>
      </div>
      <CardLabels
        v-if="showLabelsSection"
        :conversation-labels="chat.labels"
        class="mt-0.5 mx-2 mb-0"
      >
        <template v-if="hasSlaPolicyId && !isCommentCard" #before>
          <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
        </template>
      </CardLabels>
    </div>
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :priority="chat.priority"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        :conversation-labels="chat.labels"
        :conversation-url="conversationPath"
        :allowed-options="effectiveContextMenuOptions"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @remove-label="onRemoveLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @assign-priority="assignPriority"
        @delete-conversation="deleteConversation"
        @close="closeContextMenu"
      />
    </ContextMenu>
  </div>
</template>
